import React from 'react';

import { useComponentDefaultProps } from 'domain/hooks';
import { GenericHTMLProps } from './common-types';
import { noop } from 'domain/utils';
import { Key } from './Keyboard';

export type InteractiveOnClick = (
	evt:
		| React.MouseEvent<HTMLElement, MouseEvent>
		| React.KeyboardEvent<HTMLElement>
) => void;

type InteractiveElementProps = Pick<
	GenericHTMLProps,
	'onClick' | 'onContextMenu' | 'onDoubleClick' | 'onKeyDown' | 'children'
> & {
	onInteract?: InteractiveOnClick;
	disabled?: boolean;
};

const defaultProps: InteractiveElementProps = {
	disabled: false,
	onClick: noop,
	onContextMenu: noop,
	onDoubleClick: noop,
	onInteract: noop,
	onKeyDown: noop,
};

/**
 * Functional component that transforms the received child into an accessible
 * and interactive component.
 * @param props
 * @returns
 */
export const Interactive = React.forwardRef(
	(props: InteractiveElementProps, ref: any) => {
		const _props = useComponentDefaultProps(defaultProps, props);

		const onClick =
			(originalHandler?: React.MouseEventHandler) =>
			(evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
				_props.onClick?.(evt);
				originalHandler?.(evt);

				if (!_props.disabled) {
					_props.onInteract!(evt);
				}
			};

		const onDoubleClick =
			(originalHandler?: React.MouseEventHandler) =>
			(evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
				_props.onDoubleClick?.(evt);
				originalHandler?.(evt);

				if (!_props.disabled) {
					_props.onInteract!(evt);
				}
			};

		const onContextMenu =
			(originalHandler?: React.MouseEventHandler) =>
			(evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
				_props.onContextMenu?.(evt);
				originalHandler?.(evt);

				if (!_props.disabled) {
					_props.onInteract!(evt);
				}
			};

		const onKeyDown =
			(originalHandler?: React.KeyboardEventHandler) =>
			(evt: React.KeyboardEvent<HTMLElement>) => {
				_props.onKeyDown?.(evt);
				originalHandler?.(evt);
				if (checkKeyboardEvent(evt)) {
					if (!_props.disabled) {
						_props.onInteract!(evt);
						evt.preventDefault();
					}
				}
			};

		if (
			React.Children.only(_props.children) &&
			React.isValidElement(_props.children)
		) {
			let child = _props.children as any;
			return React.cloneElement<any>(child, {
				ref: child.ref ?? ref,
				onClick: onClick(child.props.onClick),
				onDoubleClick: onDoubleClick(child.props.onDoubleClick),
				onContextMenu: onContextMenu(child.props.onContextMenu),
				onKeyDown: onKeyDown(child.props.onKeyDown),
				tabIndex: _props.disabled ? -1 : child.props.tabIndex ?? 0,
				role: child.props.role ?? 'button',
				['aria-disabled']: _props.disabled,
				style: {
					...(_props as any).style,
					...child.props.style,
				},
			});
		}

		return null;
	}
);

const checkKeyboardEvent = (evt: React.KeyboardEvent<HTMLElement>): boolean => {
	return evt.code === Key.Space || evt.code === Key.Enter;
};
